// LanguageService.js

import { askGPT } from './OpenAIService';

export class LanguageConfig {
  static SUPPORTED_LANGUAGES = {
    'ar': { name: 'Arabic', rtl: true },
    'bg': { name: 'Bulgarian', rtl: false },
    'cs': { name: 'Czech', rtl: false },
    'da': { name: 'Danish', rtl: false },
    'de': { name: 'German', rtl: false },
    'el': { name: 'Greek', rtl: false },
    'en': { name: 'English', rtl: false },
    'es': { name: 'Spanish', rtl: false },
    'et': { name: 'Estonian', rtl: false },
    'fi': { name: 'Finnish', rtl: false },
    'fr': { name: 'French', rtl: false },
    'he': { name: 'Hebrew', rtl: true },
    'hr': { name: 'Croatian', rtl: false },
    'hu': { name: 'Hungarian', rtl: false },
    'id': { name: 'Indonesian', rtl: false },
    'it': { name: 'Italian', rtl: false },
    'ja': { name: 'Japanese', rtl: false },
    'ko': { name: 'Korean', rtl: false },
    'lt': { name: 'Lithuanian', rtl: false },
    'lv': { name: 'Latvian', rtl: false },
    'nl': { name: 'Dutch', rtl: false },
    'no': { name: 'Norwegian', rtl: false },
    'pl': { name: 'Polish', rtl: false },
    'pt': { name: 'Portuguese', rtl: false },
    'ro': { name: 'Romanian', rtl: false },
    'ru': { name: 'Russian', rtl: false },
    'sk': { name: 'Slovak', rtl: false },
    'sl': { name: 'Slovenian', rtl: false },
    'sv': { name: 'Swedish', rtl: false },
    'tr': { name: 'Turkish', rtl: false },
    'uk': { name: 'Ukrainian', rtl: false },
    'zh': { name: 'Chinese', rtl: false }
  };

  static isLanguageSupported(langCode) {
    return langCode in this.SUPPORTED_LANGUAGES;
  }

  static getLanguageInfo(langCode) {
    return this.SUPPORTED_LANGUAGES[langCode] || null;
  }
}

// Détection de langue utilisant DIRECTEMENT votre askGPT
export class ClientLanguageDetector {
  static cache = new Map();
  
  // Détection rapide par patterns (pour éviter les appels GPT inutiles)
  static detectLanguagePattern(text) {
    const patterns = {
      'fr': /\b(le|la|les|de|du|des|et|ou|que|qui|avec|dans|pour|sur|par|un|une|ce|cette)\b/gi,
      'en': /\b(the|and|or|of|to|in|for|with|on|by|that|this|is|are|was|were|a|an)\b/gi,
      'es': /\b(el|la|los|las|de|del|y|o|que|con|en|para|por|es|son|era|fueron)\b/gi,
      'de': /\b(der|die|das|und|oder|von|zu|in|für|mit|auf|durch|ist|sind|war)\b/gi,
      'it': /\b(il|la|lo|gli|le|di|del|e|o|che|con|in|per|su|da|è|sono|era)\b/gi,
      'pt': /\b(o|a|os|as|de|do|da|e|ou|que|com|em|para|por|é|são|era)\b/gi
    };

    let maxMatches = 0;
    let detectedLang = 'en';

    for (const [lang, pattern] of Object.entries(patterns)) {
      const matches = text.match(pattern);
      const matchCount = matches ? matches.length : 0;
      
      if (matchCount > maxMatches) {
        maxMatches = matchCount;
        detectedLang = lang;
      }
    }

    // Confiance basée sur le nombre de mots reconnus
    const confidence = Math.min(maxMatches / 5, 1); // Max 1, normalisé sur 5 mots
    return { language: detectedLang, confidence };
  }

  // Détection via votre askGPT existant
  static async detectLanguageGPT(text) {
    try {
      const prompt = `Detect the language of this text and respond with only the 2-letter ISO language code (like: en, fr, es, de, etc.):

"${text.substring(0, 300)}"

Language code:`;

      console.log('🔍 Détection langue via askGPT...');
      const response = await askGPT(prompt);
      
      // Extraire le code langue de la réponse
      const langCode = response.toLowerCase().match(/\b[a-z]{2}\b/)?.[0];
      
      return LanguageConfig.isLanguageSupported(langCode) ? langCode : null;
    } catch (error) {
      console.error('❌ Erreur détection GPT:', error);
      return null;
    }
  }

  // Méthode principale - stratégie intelligente
  static async detectLanguage(text) {
    if (!text || text.length < 5) return 'en';

    // Cache check
    const cacheKey = text.substring(0, 50);
    if (this.cache.has(cacheKey)) {
      console.log('⚡ Cache hit pour détection langue');
      return this.cache.get(cacheKey);
    }

    let finalLanguage = 'en';

    try {
      // 1. Essayer les patterns (rapide, gratuit)
      const patternResult = this.detectLanguagePattern(text);
      
      // 2. Si confiance faible OU texte long, utiliser GPT
      if (patternResult.confidence < 0.4 || text.length > 150) {
        console.log('🤖 Utilisation GPT pour détection langue précise...');
        const gptResult = await this.detectLanguageGPT(text);
        finalLanguage = gptResult || patternResult.language;
      } else {
        console.log('⚡ Détection rapide par patterns');
        finalLanguage = patternResult.language;
      }

      // Cache le résultat
      this.cache.set(cacheKey, finalLanguage);
      
      // Limite du cache
      if (this.cache.size > 200) {
        const firstKey = this.cache.keys().next().value;
        this.cache.delete(firstKey);
      }

    } catch (error) {
      console.error('❌ Erreur détection langue:', error);
      finalLanguage = 'en';
    }

    return finalLanguage;
  }

  // Analyse contextuelle via askGPT
  static async analyzeLanguageContext(query, userLanguage) {
    try {
      const prompt = `Analyze the language context of this query: "${query}"

Return a JSON response with detected languages and cultural context:
{
  "detectedLanguages": [{"code": "language_code", "relevance": 0.8}],
  "culturalContext": {"regions": ["Europe", "France"], "topics": ["technology"]}
}

Response:`;

      const response = await askGPT(prompt);
      
      // Essayer de parser le JSON de la réponse
      try {
        // Nettoyer la réponse pour extraire le JSON
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          return JSON.parse(jsonMatch[0]);
        }
      } catch (parseError) {
        console.warn('⚠️ Erreur parsing JSON contexte:', parseError);
      }

      // Fallback
      return {
        detectedLanguages: [{ code: userLanguage, relevance: 1 }],
        culturalContext: { regions: [], topics: [] }
      };

    } catch (error) {
      console.error('❌ Erreur analyse contexte:', error);
      return {
        detectedLanguages: [{ code: userLanguage, relevance: 1 }],
        culturalContext: { regions: [], topics: [] }
      };
    }
  }
}

// Distribution (logique côté client - pas besoin de GPT)
export class LanguageDistributor {
  static async calculateDistribution(query, userLanguage, userCountry) {
    try {
      const context = await ClientLanguageDetector.analyzeLanguageContext(query, userLanguage);
      const distribution = new Map();

      // Poids de base
      distribution.set(userLanguage, 0.4);
      if (userLanguage !== 'en') {
        distribution.set('en', 0.3);
      }

      // Ajouter langues détectées
      context.detectedLanguages?.forEach(lang => {
        if (LanguageConfig.isLanguageSupported(lang.code)) {
          const currentWeight = distribution.get(lang.code) || 0;
          distribution.set(lang.code, currentWeight + (lang.relevance * 0.3));
        }
      });

      return this.normalizeDistribution(distribution);
    } catch (error) {
      console.error('❌ Erreur calcul distribution:', error);
      return new Map([[userLanguage, 0.7], ['en', 0.3]]);
    }
  }

  static normalizeDistribution(distribution) {
    const total = Array.from(distribution.values()).reduce((sum, weight) => sum + weight, 0);
    const normalized = new Map();
    for (const [lang, weight] of distribution) {
      normalized.set(lang, weight / total);
    }
    return normalized;
  }

  static getSearchQuota(distribution, maxResults) {
    const quotas = new Map();
    let remainingResults = maxResults;

    for (const [lang, weight] of distribution) {
      const quota = Math.max(1, Math.floor(maxResults * weight));
      quotas.set(lang, quota);
      remainingResults -= quota;
    }

    if (remainingResults > 0) {
      const sortedLangs = Array.from(distribution.entries())
        .sort(([, a], [, b]) => b - a);
      for (const [lang] of sortedLangs) {
        if (remainingResults <= 0) break;
        quotas.set(lang, quotas.get(lang) + 1);
        remainingResults--;
      }
    }

    return quotas;
  }
}

// Traduction via askGPT
export class LanguageProcessor {
  static async translateText(text, targetLang, sourceLang = null) {
    if (!text || !targetLang) return text;

    try {
      const languageNames = {
        'fr': 'French', 'en': 'English', 'es': 'Spanish', 'de': 'German',
        'it': 'Italian', 'pt': 'Portuguese', 'ru': 'Russian', 'zh': 'Chinese',
        'ja': 'Japanese', 'ko': 'Korean', 'ar': 'Arabic'
      };

      const targetLanguageName = languageNames[targetLang] || 'English';
      const sourceInfo = sourceLang ? ` from ${languageNames[sourceLang] || 'unknown'}` : '';

      const prompt = `Translate this text${sourceInfo} to ${targetLanguageName}:

"${text}"

Translation:`;

      console.log(`🌐 Traduction vers ${targetLanguageName}...`);
      const response = await askGPT(prompt);
      return response.trim();

    } catch (error) {
      console.error('❌ Erreur traduction:', error);
      return text;
    }
  }

  static normalizeText(text, lang) {
    try {
      if (LanguageConfig.SUPPORTED_LANGUAGES[lang]?.rtl) {
        text = `\u202B${text}\u202C`; // Marqueurs RTL
      }
      return text.normalize('NFKC').replace(/\s+/g, ' ').trim();
    } catch (error) {
      return text;
    }
  }
}

// Service principal
export class ClientLanguageService {
  constructor(defaultLanguage = 'en') {
    this.defaultLanguage = defaultLanguage;
  }

  async initializeSearch(query, userLanguage, userCountry, maxResults) {
    const distribution = await LanguageDistributor.calculateDistribution(
      query, userLanguage, userCountry
    );
    const searchQuotas = LanguageDistributor.getSearchQuota(distribution, maxResults);

    return {
      distribution: Object.fromEntries(distribution),
      quotas: Object.fromEntries(searchQuotas),
      detectedLanguage: await ClientLanguageDetector.detectLanguage(query)
    };
  }

  async processSearchResult(result, targetLanguage) {
    const detectedLang = await ClientLanguageDetector.detectLanguage(
      result.title + ' ' + result.snippet
    );
    
    if (detectedLang === targetLanguage) {
      return { ...result, language: detectedLang };
    }

    const [translatedTitle, translatedSnippet] = await Promise.all([
      LanguageProcessor.translateText(result.title, targetLanguage, detectedLang),
      LanguageProcessor.translateText(result.snippet, targetLanguage, detectedLang)
    ]);

    return {
      ...result,
      originalTitle: result.title,
      originalSnippet: result.snippet,
      title: translatedTitle,
      snippet: translatedSnippet,
      language: detectedLang
    };
  }

  isSupported(langCode) {
    return LanguageConfig.isLanguageSupported(langCode);
  }

  getLanguageInfo(langCode) {
    return LanguageConfig.getLanguageInfo(langCode);
  }

  // Debug et utilitaires
  getCacheStats() {
    return {
      cacheSize: ClientLanguageDetector.cache.size,
      samples: Array.from(ClientLanguageDetector.cache.entries()).slice(0, 3)
    };
  }

  clearCache() {
    ClientLanguageDetector.cache.clear();
    console.log('🧹 Cache langue vidé');
  }

  // Méthode statique pour usage direct
  static async detectLanguage(text) {
    return await ClientLanguageDetector.detectLanguage(text);
  }
}

// Instance par défaut
const LanguageService = new ClientLanguageService();
export default LanguageService;